import { FormikProps, getIn } from 'formik';
import React, { useContext } from 'react';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import styled, { css } from 'styled-components/macro';
import { formatCurrency } from '../../helpers/stringHelpers';
import { ReactComponent as DeleteIcon } from '../../images/icons/delete.svg';
import { ReactComponent as DragHandleIcon } from '../../images/icons/drag-handle.svg';
import { ButtonIcon, SecondaryButton } from '../../shared/Button';
import { FormContext } from '../../shared/form/Form';
import { TextInputField } from '../../shared/form/inputs/TextInputField';
import { Tr } from '../../shared/Table';
import { shadow5 } from '../../styling/shadows';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { useBillOfMaterialsPricingModelInfo } from './BillOfMaterialsContext';
import { BillOfMaterialsItemFormModel, itemIsDesignCharge } from './billOfMaterialsItemFormModel';
import {
  ActionButtonContainer,
  ActionButtonTd,
  CurrencyTd,
  DescriptionTd,
  DragHandleTd,
  InactivePartErrorIndicator,
  PartCodeContainer,
  PartCodeTd,
  QuantityTd,
} from './BillOfMaterialsItemsTable';
import { BillOfMaterialsPartsById, getPartPriceBasedOnCustomerType } from './dataForEditBillOfMaterialsForm';

type Props = {
  fieldName: string;
  item: BillOfMaterialsItemFormModel;
  partsById: BillOfMaterialsPartsById;
  deleteItem: () => void;
  errors: FormikProps<EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel>['errors'];
  draggableProvided: DraggableProvided;
  draggableSnapshot: DraggableStateSnapshot;
};

export const BillOfMaterialsItemField = ({
  fieldName,
  item,
  partsById,
  deleteItem,
  errors,
  draggableProvided,
  draggableSnapshot,
}: Props) => {
  const { isUsingLegacyPricingModel, selectedCustomerTypeOption } = useBillOfMaterialsPricingModelInfo();

  const part = item.partId == null ? null : partsById[item.partId] || null;

  const quantityAsNumber = isNaN(Number(item.quantity)) ? null : Number(item.quantity);

  const partIsInactiveValidationFieldName = `${fieldName}.partIsInactiveValidation`;
  const partIsInactiveValidationError = getIn(errors, partIsInactiveValidationFieldName);

  const { isDisabled } = useContext(FormContext);

  const canDelete = !getIn(errors, `${fieldName}.quantity`) && !itemIsDesignCharge(item, partsById);

  if (part == null) {
    throw new Error(`Couldn't find Part with ID ${item.partId}`);
  }

  const costInPounds = item.costInPounds || part.costInPounds;

  const priceInPounds =
    item.priceInPounds || getPartPriceBasedOnCustomerType(isUsingLegacyPricingModel, part, selectedCustomerTypeOption);

  return (
    <DraggableTr
      {...draggableProvided.draggableProps}
      ref={draggableProvided.innerRef}
      $isDragging={draggableSnapshot.isDragging}
    >
      <DragHandleTd>
        {!isDisabled && (
          <div {...draggableProvided.dragHandleProps}>
            <DragHandleIcon />
          </div>
        )}
      </DragHandleTd>
      <PartCodeTd>
        <PartCodeContainer $hasError={partIsInactiveValidationError != null}>
          {part.name}
          <InactivePartErrorIndicator
            fieldName={partIsInactiveValidationFieldName}
            showError={partIsInactiveValidationError != null}
            error={partIsInactiveValidationError}
          />
        </PartCodeContainer>
      </PartCodeTd>
      <QuantityTd>
        <TextInputField name={`${fieldName}.quantity`} label={null} />
      </QuantityTd>
      <DescriptionTd>{part.description}</DescriptionTd>
      <CurrencyTd>
        {quantityAsNumber == null || costInPounds == null ? null : formatCurrency(quantityAsNumber * costInPounds)}
      </CurrencyTd>
      <CurrencyTd>
        {quantityAsNumber == null || priceInPounds == null ? null : formatCurrency(quantityAsNumber * priceInPounds)}
      </CurrencyTd>
      <ActionButtonTd>
        {!isDisabled && (
          <ActionButtonContainer>
            <SecondaryButton onClick={deleteItem} disabled={!canDelete} title="Delete">
              <ButtonIcon>
                <DeleteIcon title="Delete" data-testid={deleteBillOfMaterialsItemButtonTestId(fieldName)} />
              </ButtonIcon>
            </SecondaryButton>
          </ActionButtonContainer>
        )}
      </ActionButtonTd>
    </DraggableTr>
  );
};

export const deleteBillOfMaterialsItemButtonTestId = (fieldName: string) => `delete-bom-item-${fieldName}`;

const draggingStyles = css`
  display: table;
  table-layout: fixed;
  box-shadow: ${shadow5};
`;

const DraggableTr = styled(Tr)<{ $isDragging: boolean }>`
  ${props => props.$isDragging && draggingStyles};
`;
